/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Day,
  Week,
  Month,
  Agenda,
  ScheduleComponent,
  Inject,
  Resize,
  DragAndDrop,
  ViewDirective,
  ViewsDirective,
  RenderCellEventArgs,
  View,
} from '@syncfusion/ej2-react-schedule';
import { useDispatch, useSelector } from 'react-redux';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import { Box, Typography } from '@mui/material';
import { RootState } from 'packages/blu-booking/src/store';
import { getConfig } from '../../../utility_objects/WhiteCache';
import {
  AppointmentMouseDown,
  AppointmentMouseUp,
} from '../../../utility_objects/AppointmentMouseUp';
import { isOpen } from '../utility_objects/work-hours';
import {
  CellStatus,
  WorkHoursCellTemplate,
} from '../../../UI/WorkHoursCellTemplate/WorkHoursCellTemplate';
import {
  PersonalCalendarSetAgendaColors,
  PersonalCalendarSetSelectedDate,
} from '../store';
import { setPrintComponent } from '../../../store/Appointment';
import { appointmentFormPopupOpen } from '../../../utility_objects/AppointmentFormPopupOpen';
import { CalendarEventQuickInfo } from '../../../UI/CalendarEventQuickInfo/CalendarEventQuickInfo';
import { AppointmentRender } from '../../../utility_objects/AppointmentRender';
import { AppointmentResize } from '../../../utility_objects/AppointmentResize';
import { AppointmentDragDrop } from '../../../utility_objects/AppointmentDragDrop';
import { CalendarEventTemplate } from '../../../UI/CalendarEventTemplate/CalendarEventTemplate';
import { useSchedulerData } from '../hooks/useSchedulerData';
import { useSchedulerPersistence } from '../../../hooks/useSchedulerPersistence';
import { useSchedulerRefresh } from '../hooks/useSchedulerRefresh';
import { useWorkWeekColumns } from '../hooks/useWorkWeekColumns';
import { CalendarLoader } from '../../../UI/CalendarLoader/CalendarLoader';
import { formatOraSyncfusion } from '../../../hooks/useFinestraOraria';

interface SchedulerProps {
  calendarPerUser: any;
  shiftPerUser: any;
  /**
   * Ore mostrate dalle viste a griglia oraria, intere (useFinestraOraria in
   * PersonalCalendarInner): 5-24 con Ore «Tutte» nel riquadro Visualizzazione
   */
  startHour: number;
  endHour: number;
  /**
   * Nessuna finestra ancora, alla prima apertura con Ore «Prenotabili»: il
   * calendario resta nascosto sotto il loader invece di mostrare le 5-24
   */
  finestraInAttesa: boolean;
  /** Ore «Prenotabili» e dati non ancora pronti: il loader resta aperto */
  finestraProvvisoria: boolean;
}

export const Scheduler = memo(function Scheduler({
  calendarPerUser,
  shiftPerUser,
  startHour,
  endHour,
  finestraInAttesa,
  finestraProvvisoria,
}: SchedulerProps): JSX.Element {
  const componentRef = useRef();
  const dispatch = useDispatch();
  const scheduleObj = useRef<ScheduleComponent>(null);

  // ─── Redux state ───────────────────────────────────────────────

  const {
    selectedDate,
    view,
    currentView,
    selectedUsers,
    selectedStudi,
    selectedServices,
    zoom,
  } = useSelector((state: RootState) => state.bluBookingPersonalCalendar);

  const { sessionProfilo, focus10 } = getConfig();

  // ─── Data fetching ─────────────────────────────────────────────
  const { appointments, holidays, isLoadingCalendarOrHolidays } =
    useSchedulerData();

  // ─── Agenda colors ────────────────────────────────────────────
  useEffect(() => {
    if (isLoadingCalendarOrHolidays) return;

    let union: string[] = [];
    if (calendarPerUser) union = [...(calendarPerUser?.keys ?? [])];
    if (shiftPerUser) {
      union = [...union, ...(shiftPerUser?.keys ?? [])];
    }
    union = [...new Set(union)];
    dispatch(PersonalCalendarSetAgendaColors(union));
  }, [isLoadingCalendarOrHolidays, calendarPerUser, dispatch, shiftPerUser]);

  // Union per popupOpen (calcolato dal render, non da useEffect)
  let union: string[] = [];
  if (calendarPerUser) union = [...(calendarPerUser?.keys ?? [])];
  if (shiftPerUser) {
    union = [...union, ...(shiftPerUser?.keys ?? [])];
  }
  union = [...new Set(union)];

  const getCellStatus = useCallback(
    (date: Date, type: string): CellStatus => {
      const [, color, code] = isOpen(date, type, calendarPerUser, shiftPerUser);
      return { code: code as number, color: color as string };
    },
    [calendarPerUser, shiftPerUser]
  );

  const cellTemplate = useCallback(
    (props: any) => (
      <WorkHoursCellTemplate
        date={props.date}
        type={props.type}
        groupIndex={props.groupIndex}
        getStatus={getCellStatus}
      />
    ),
    [getCellStatus]
  );

  // ─── Syncfusion lifecycle ──────────────────────────────────────
  useSchedulerPersistence(scheduleObj, {
    calendario: 'personal',
    selectedDate,
    focus10,
    startHour,
    endHour,
  });
  useWorkWeekColumns(
    view,
    selectedDate,
    calendarPerUser,
    'personal-schedule',
    isLoadingCalendarOrHolidays,
    startHour,
    endHour
  );

  const onActionComplete = (args: any) => {
    if (args.requestType === 'dateNavigate') {
      const schedulerInstance = scheduleObj.current;
      const newDate = schedulerInstance?.selectedDate;
      if (newDate) dispatch(PersonalCalendarSetSelectedDate(newDate));
    }
  };

  const getWorkDaysStartEndHours = useCallback(
    (args: RenderCellEventArgs) => {
      if (
        args.elementType === 'workCells' ||
        args.elementType === 'monthCells'
      ) {
        args.element.id =
          args.date !== null
            ? dayjs(args.date).format('DD/MM/YYYY HH:mm') ?? ''
            : '';
        args.element.addEventListener('mousedown', AppointmentMouseDown, {
          capture: true,
        });
        args.element.addEventListener(
          'mouseup',
          (event: any) => AppointmentMouseUp(event, 'personal'),
          {
            capture: true,
          }
        );

        // posid attribute per la logica popup
        const [posId] = isOpen(
          args.date,
          args.elementType,
          calendarPerUser,
          shiftPerUser
        );
        if (posId !== '') args.element.setAttribute('posid', posId as string);
      } else if (args.elementType === 'majorSlot') {
        args.element.setAttribute('style', 'font-size:14px; font-weight: 500;');
      }
    },
    [calendarPerUser, shiftPerUser] // Dipendenze aggiornate: usa i dati dinamici
  );

  // ─── Templates & config ───────────────────────────────────────
  function dateHeaderTemplate(props: any) {
    const date: any = dayjs(props.date);
    const dateFormated = date.format('ddd D');
    const mounthFormated = date.format('MMM');

    return (
      <div
        style={{
          width: '100%',
          textAlign: 'center',
          cursor: 'pointer',
          overflow: 'hidden',
        }}
        onClick={(event) => {
          dispatch(PersonalCalendarSetSelectedDate(props.date));
          event.stopPropagation();
        }}
      >
        <Typography
          fontSize="14px"
          fontWeight="500"
          sx={{ color: 'unset' }}
        >{`${dateFormated.charAt(0).toUpperCase()}${dateFormated.slice(1)} ${
          date.date() === 1 && view !== 'Day'
            ? `${mounthFormated.charAt(0).toUpperCase()}${mounthFormated.slice(
                1
              )}`
            : ''
        }`}</Typography>
      </div>
    );
  }

  const getZoom = useCallback(() => {
    return {
      enable: true,
      interval: zoom?.interval,
      slotCount: zoom?.slotCount,
    };
  }, [zoom]);

  useEffect(() => {
    dispatch(setPrintComponent(componentRef.current));
  }, [componentRef, dispatch]);

  const getModules = useMemo(() => {
    if (!sessionProfilo.agendaModifica) return [Day, Week, Month, Agenda];
    return [Day, Week, Month, Agenda, Resize, DragAndDrop];
  }, [sessionProfilo.agendaModifica]);

  // ─── Data refresh ──────────────────────────────────────────────
  useSchedulerRefresh(
    scheduleObj,
    appointments,
    holidays,
    isLoadingCalendarOrHolidays
  );

  // ─── Render ───────────────────────────────────────────────────
  return (
    <Box
      id="personal-schedule"
      ref={componentRef}
      height={'100%'}
      width={'100%'}
      // Solo il calendario: il loader, qui dentro, deve restare visibile
      sx={
        finestraInAttesa
          ? { '& > .e-schedule': { visibility: 'hidden' } }
          : undefined
      }
    >
      <CalendarLoader
        open={isLoadingCalendarOrHolidays || finestraProvvisoria}
      />

      <ScheduleComponent
        id="-personal-schedule-persistence"
        ref={scheduleObj}
        width="100%"
        timeScale={getZoom()}
        height="100%"
        cssClass="personal-schedule-cell-dimension"
        selectedDate={selectedDate}
        currentView={currentView as View}
        dateFormat="dd/MM/yyyy"
        locale="it"
        minDate={new Date(2000, 0, 1)}
        enablePersistence={true}
        actionComplete={onActionComplete}
        eventSettings={{
          // dataSource gestito da useSchedulerRefresh
          template: (props: any) => <CalendarEventTemplate {...props} />,
        }}
        popupOpen={(args) => {
          if (args.data.IsAllDay) {
            args.cancel = true;

            return;
          }
          let pointOfSaleId = undefined;
          if (args.type === 'Editor') {
            if (calendarPerUser?.multi || shiftPerUser?.multi) {
              const temp = document.querySelector(
                `[data-date="${new Date(args.data.StartTime).getTime()}"]`
              );
              if (temp !== null) {
                pointOfSaleId = temp.getAttribute('posid');
              }
            } else if (union.length > 0) {
              pointOfSaleId = union[0];
            }
          }

          appointmentFormPopupOpen(
            args,
            selectedUsers,
            selectedStudi,
            selectedServices,
            pointOfSaleId as string | undefined
          );
        }}
        dragStart={(args: any) => {
          if (args.data.IsAllDay) {
            args.cancel = true;
            return;
          }
        }}
        dragStop={(args: any) => {
          AppointmentDragDrop(args, appointments, 'personal');
        }}
        resizeStop={(args: any) => {
          if (args.data.IsAllDay) return;
          AppointmentResize(args, appointments);
        }}
        dateHeaderTemplate={dateHeaderTemplate}
        firstDayOfWeek={1}
        renderCell={getWorkDaysStartEndHours}
        cellTemplate={cellTemplate}
        startHour={formatOraSyncfusion(startHour)}
        endHour={formatOraSyncfusion(endHour)}
        eventRendered={(args) => AppointmentRender(args)}
        workDays={[0, 1, 2, 3, 4, 5, 6]}
        quickInfoTemplates={
          {
            header: (_: any) => null,
            content: (props: any) => <CalendarEventQuickInfo {...props} />,
          } as any
        }
      >
        <ViewsDirective>
          <ViewDirective option="Day" interval={view === '3Days' ? 3 : 1} />
          <ViewDirective option="Week" />
          <ViewDirective option="Month" />
          <ViewDirective option="Agenda" />
        </ViewsDirective>
        <Inject services={getModules} />
      </ScheduleComponent>
    </Box>
  );
});
